<template>
  <b-modal
    v-model="show"
    centered
    size="md-lg"
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="bg-purple"
  >
    <template v-slot:modal-header>
      <div
        class="w-100 d-flex flex-row justify-content-between align-items-center"
      >
        <h5 class="modal-title">{{ formTitle }}</h5>
        <button
          type="button"
          aria-label="Close"
          class="close text-white"
          style="opacity: 1 !important"
          @click="$emit('onHidden')"
        >
          ×
        </button>
      </div>
    </template>
    <template v-slot:modal-footer>
      <div
        v-if="payment && type === 'view'"
        class="w-100 d-flex justify-content-between"
      >
        <div>
          <b-button size="md" @click="deleteTransaction" variant="danger">
            Delete Transaction
          </b-button>
          <b-button
            size="md"
            class="ml-2"
            @click="showRefundType"
            variant="primary"
            v-if="payment.type !== 'Refund'"
          >
            Refund
          </b-button>
        </div>
        <div>
          <b-button size="md" @click="$emit('onHidden')" variant="secondary">
            Close
          </b-button>
        </div>
      </div>
      <div v-else class="d-flex w-full justify-content-end items-center">
        <div class="text-right mt-1 mr-2">
          <b-button size="md" @click="$emit('onHidden')" variant="secondary">
            Cancel
          </b-button>
          <b-button
            :disabled="
              formErrors.invoicesRefund.length > 0 ||
                !totalRefundAmount ||
                !hasRefundInvoicesWithAmount ||
                payment.type === 'Refund'
            "
            size="md"
            class="ml-2"
            @click="refund"
            variant="primary"
          >
            Refund
          </b-button>
        </div>
      </div>
    </template>
    <div class="payment-details-container p-2 lower-font-size">
      <div class="font-weight-bold">
        Payment details
      </div>
      <hr class="my-2" />
      <div class="allocation-container">
        <div class="d-flex mb-3 justify-content-between">
          <div class="p-2">
            <div class="">
              <div class="mb-2">Patient:</div>
              <div>
                {{
                  formatClient(
                    payment.patient.firstName,
                    payment.patient.middleName,
                    payment.patient.preferredName,
                    payment.patient.lastName,
                    payment.patient.mrn
                  )
                }}
              </div>
            </div>
          </div>
          <div class="p-2">
            <div class="">
              <div class="mb-2">Type:</div>
              <div class="">
                {{ payment.type }}
              </div>
            </div>
          </div>
          <div class="p-2">
            <div class="">
              <div class="mb-2">Date:</div>
              <div class="">
                {{ formatDateOnly(payment.paymentDate) }}
              </div>
            </div>
          </div>
          <div class="p-2">
            <div class="">
              <div class="mb-2">Method:</div>
              <div class="">{{ getPaymentMethod() }}</div>
            </div>
          </div>
          <div class="p-2">
            <div class="text-right">
              <div class="mb-2">Payment Amount:</div>
              <div class="">{{ toCurrencyValue(payment.totalAmount) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="payment && type === 'view'"
      class="payment-details-container p-2 lower-font-size"
    >
      <b>Payment allocations:</b>
      <hr class="my-2" />

      <div class="allocation-container">
        <div class="d-flex mb-3 justify-content-between">
          <div class="p-2">
            <div class="">
              <div class="mb-2">Invoice:</div>
              <div
                v-for="(allocation, index) in payment.transactionsAllocations"
                :key="index"
              >
                <div class="">
                  {{ allocation.invoice.refNumber }}
                </div>
              </div>
            </div>
          </div>
          <div class="p-2">
            <div class="">
              <div class="mb-2">Details:</div>
              <div
                v-for="(allocation, index) in payment.transactionsAllocations"
                :key="index"
              >
                <div
                  v-for="(invoiceLineItem, lineItemIndex) in allocation.invoice
                    .lineItems"
                  :key="lineItemIndex"
                >
                  {{ invoiceLineItem.description }}
                </div>
              </div>
            </div>
          </div>
          <div class="p-2">
            <div class="text-right">
              <div class="mb-2">Amount:</div>
              <div
                v-for="(allocation, index) in payment.transactionsAllocations"
                :key="index"
              >
                <div class="">{{ toCurrencyValue(allocation.amount) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="payment-details-container p-2 lower-font-size">
      <b>Refund details</b>
      <hr class="my-2" />
      <div class="d-flex">
        <div style="width: 250px;">
          <div>Method:</div>
          <v-select
            placeholder="Transaction method"
            v-model="selectedRefundMethod"
            :options="getAvailablePaymentMethods"
            :clearable="false"
          >
          </v-select>
        </div>
        <div class="ml-5">
          <div>Available refund amount:</div>
          <div class="text-right font-weight-bold">
            {{ payment && toCurrencyValue(payment.refundableAmount) }}
          </div>
        </div>
      </div>
      <div class="mt-5">
        <b>Enter refund amounts</b>
        <b-table
          class="bg-white mt-3"
          :items="refundItems"
          :fields="refundFields"
        >
          <template #table-colgroup="scope">
            <col
              v-for="field in scope.fields"
              :key="field.key"
              :style="{ width: field.key === 'amount' ? '150px' : '' }"
            />
          </template>
          <template #cell(invoice)="row">
            <b-form-checkbox v-model="row.item.invoice.refNumber" />
          </template>
          <template #cell(details)="row">
            <span v-if="row.item.details.length > 0">
              {{ row.item.details.length }} Item:
            </span>
            <span v-else> This invoice has no services </span>
            <div v-for="(item, index) in row.item.details" :key="index">
              {{ item.description }}
            </div>
          </template>
          <template #cell(paid)="row">
            {{
              toCurrencyValue(
                row.item.totalAmount == row.item.amountPaid
                  ? row.item.totalAmount
                  : row.item.amountPaid
              )
            }}
          </template>
          <template #cell(amount)="row">
            <b-form-group
              class="text-center"
              :state="formErrors.invoicesRefund.length > 0 ? false : null"
              :invalid-feedback="
                formErrors.invoicesRefund
                  .map(error => {
                    if (error.index === row.index) {
                      return error.message;
                    }
                  })
                  .join(', ')
              "
            >
              <b-form-input
                v-model="row.item.amount"
                type="number"
                min="0"
                required
                :id="'input-amount-' + row.index"
              />
            </b-form-group>
          </template>
        </b-table>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from "vuex";
import paymentServer from "../../../server/payment";
import patientServer from "../../../server/patient";

export default {
  name: "PaymentViewRefundModal",
  props: ["paymentId", "formTitle", "type"],
  data() {
    return {
      payment: null,
      refundFields: [
        { key: "refNumber", label: "Invoice" },
        { key: "details", label: "Details" },
        { key: "paid", label: "Paid" },
        { key: "amount", label: "Refund Amount" }
      ],
      selectedRefundMethod: null,
      refundAmount: null,
      paymentMethods: [
        {
          label: "Online",
          value: "online"
        },
        {
          label: "External",
          value: "external"
        },
        {
          label: "Cash",
          value: "cash"
        },
        {
          label: "Check",
          value: "check"
        }
      ],
      refundItems: [],
      formErrors: {
        invoicesRefund: []
      }
    };
  },
  async mounted() {
    const response = await paymentServer.get(this.paymentId);
    this.payment = response;
    this.refundItems = this.payment.transactionsAllocations.map(
      transactionAlocation => ({
        ...transactionAlocation.invoice,
        refNumber: transactionAlocation.invoice.refNumber,
        details: transactionAlocation.invoice.lineItems,
        amountPaid: transactionAlocation.refundableAmount,
        amount: "0"
      })
    );
    this.selectedRefundMethod = this.getAvailablePaymentMethods[0];
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError"]),
    async refund() {
      const refundData = {
        patientId: this.payment.patientId,
        type: "Refund",
        method: this.selectedRefundMethod.value,
        paymentDate: new Date(),
        invoices: this.refundItems
          .map(item => ({
            ...item,
            amount: parseFloat(item.amount)
          }))
          .filter(item => item.amount > 0),
        totalAmount: parseFloat(this.totalRefundAmount),
        relatedTransactionId: this.payment.id,
        payaPaymentReferenceNumber: this.payment.payaPaymentReferenceNumber,
        cardInfoKey: this.payment.cardInfo?.ccInfoKey
      };
      this.$dialog
        .confirm(
          {
            title: "Do you want to refund this payment?"
          },
          {
            view: "confirm",
            options: [{ value: true, label: "Yes" }]
          }
        )
        .then(async () => {
          try {
            const response = await paymentServer.create(refundData);
            if (response.id) {
              this.showAlertSuccess();
              this.$emit("success");
              this.showSuccessPayment();
              this.show = false;
            }
          } catch (err) {
            const data = err.response.data;
            if (data.response.result === "110") {
              this.$dialog
                .confirm(
                  {
                    title:
                      "This is a posible duplicated payment, do you want to continue?"
                  },
                  {
                    view: "confirm",
                    options: [{ value: true, label: "Yes" }]
                  }
                )
                .then(async () => {
                  const response = await paymentServer.create({
                    ...refundData,
                    forceTransaction: "True"
                  });
                  if (response.id) {
                    this.showAlertSuccess();
                    this.$emit("success");
                    this.showSuccessPayment();
                    this.show = false;
                  }
                })
                .catch(err => {
                  const data = err.response.data;
                  this.showErrorPayment(data);
                });
            } else {
              this.showErrorPayment(data);
            }
          }
        })
        .catch(() => {});
    },
    showSuccessPayment() {
      this.$dialog.confirm(
        {},
        {
          view: "payment-result",
          data: {
            patient: this.formatClient(
              this.payment.patient.firstName,
              this.payment.patient.middleName,
              this.payment.patient.preferredName ?? "",
              this.payment.patient.lastName
            ),
            amount: this.toCurrencyValue(this.totalRefundAmount),
            method: this.formatedPaymentMethod,
            paymentType: "Refund"
          },
          success: true
        }
      );
    },

    showErrorPayment(data) {
      this.$dialog.confirm(
        {},
        {
          view: "payment-result",
          data: data,
          success: false
        }
      );
    },
    async fetchPatient() {
      this.patient = await patientServer.get(this.patientId);
    },
    getPaymentMethod() {
      let method;
      switch (this.payment.method) {
        case 0:
          method = `Credit/Debit Card ***${this.payment.cardInfo?.lastFour}`;
          break;
        case 1:
          method = "Cash";
          break;
        case 2:
          method = `Check${
            this.payment.checkNumber ? " - " + this.payment.checkNumber : ""
          }`;
          break;
        case 3:
          method = `External${
            this.payment.externalMethod
              ? " - " + this.payment.externalMethod
              : ""
          }`;
          break;
        default:
          break;
      }
      return method;
    },
    deleteTransaction() {
      this.$dialog
        .alert(
          {
            title: "Are you sure you want to delete this transaction?"
          },
          {
            view: "confirm-delete"
          }
        )
        .then(async () => {
          paymentServer.delete(this.paymentId);
          this.$emit("onHidden");
        });
    },
    showRefundType() {
      this.$emit("changeType");
    }
  },
  computed: {
    show: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    formatedPaymentMethod() {
      switch (this.selectedRefundMethod.value) {
        case "online":
          return `${this.payment.cardInfo?.cardType} ***${this.payment.cardInfo?.lastFour}`;
        case "cash":
          return "Cash";
        case "check":
          return "Check";
        case "external":
          return "External Method";
        default:
          return "";
      }
    },
    paymentAmount() {
      return this.form.invoices.reduce((accumulator, invoice) => {
        if (invoice.selected) {
          return accumulator + parseFloat(invoice.amount);
        } else {
          return accumulator;
        }
      }, 0);
    },
    totalRefundAmount() {
      return this.refundItems.reduce((total, item) => {
        return isNaN(parseFloat(item.amount))
          ? total
          : total + parseFloat(item.amount);
      }, 0);
    },
    hasRefundInvoicesWithAmount() {
      return this.refundItems.some(refundItem => refundItem.amount > 0);
    },
    getAvailablePaymentMethods() {
      let paymentMethods = this.paymentMethods.filter(
        method => method.value !== "online"
      );
      if (this.payment?.method == 0) {
        paymentMethods = [
          {
            value: "online",
            label: `${this.payment.cardInfo?.cardType} ***${this.payment.cardInfo?.lastFour}`
          },
          ...paymentMethods
        ];
      }
      return paymentMethods;
    }
  },
  watch: {
    refundItems: {
      handler(newVal) {
        this.formErrors.invoicesRefund = [];
        newVal.forEach((invoice, index) => {
          if (invoice.amount > invoice.amountPaid) {
            const errorMessage =
              "Amount can't be greater than: " +
              `${this.toCurrencyValue(invoice.amountPaid)}`;
            const errorObject = {
              index: index,
              message: errorMessage
            };
            this.formErrors.invoicesRefund.push(errorObject);
          }
          if (invoice.amount < 0) {
            const errorMessage = "This field must have a positive value";
            const errorObject = {
              index: index,
              message: errorMessage
            };
            this.formErrors.invoicesRefund.push(errorObject);
          }
        });
      },
      deep: true
    }
  }
};
</script>

<style lang="scss" scoped>
payment-details-container {
  background: rgba(255, 255, 255, 0);
}
</style>
